import { createReducer, on } from '@ngrx/store';
import { Ticket } from '../../../core/api/responses/booking-portal-login';
import { clearUserAction, logout } from '../../../core/user/user.actions';
import { dayjs } from '../../../dayjs.lib';
import { EventCalendar } from '../models/event-calendar.model';
import {
	bookingReceived,
	clearEventDates,
	clearOpenEntitlement,
	eventDataReceived,
	resetSwFailedCount,
	setNewBookingDateTime,
	setOpenEntitlement,
} from './bookings.actions';
import { Booking, BookingsState, initialState } from './bookings.state';

export interface BookingReceivedAction {
	orderId: string;
	tickets: Ticket[];
	swFailedCount?: number;
}

/**
 * called when we receive new bookings, does some flattening of the data
 */
const onBookingReceived = on(bookingReceived, (state: BookingsState, action: BookingReceivedAction) => {
	const existing = state?.bookings?.find((b) => b.orderId === action.orderId);
	const bookings: BookingReceivedAction = {
		orderId: action.orderId,
		tickets: action.tickets.map((ticket) => ({
			...ticket,
			entitlements: ticket.TICKET_ENTITLEMENTS?.[0].TICKET_ENTITLEMENT.map((entitlement) => ({
				...entitlement,
				booking: entitlement.TICKET_BOOKINGS?.[0]?.TICKET_BOOKING?.[0] ?? null,
			})),
		})),
	};

	let newBookingsState: Booking[];

	if (existing) {
		newBookingsState = [bookings];
	} else {
		newBookingsState = [...state.bookings, bookings];
	}

	return {
		...state,
		bookings: newBookingsState,
		...(action.swFailedCount && { swFailedCount: action.swFailedCount }),
	};
});

const onSetOpenEntitlement = on(setOpenEntitlement, (state: BookingsState, action) => {
	return {
		...state,
		openEntitlement: {
			ticket: action.ticket,
			entitlement: action.entitlement,
		},
	};
});

const onEventDataReceived = on(eventDataReceived, (state: BookingsState, action) => {
	const foundEvent = state.events.find((e) => e.event_id === action.event.event_id);
	if (foundEvent) {
		if (
			foundEvent.dates.find((d) => {
				const dateA = dayjs(d.date).startOf('day');
				const dateB = dayjs(action.event?.dates?.[0]?.date).startOf('day');
				return dateA.isSame(dateB);
			})
		) {
			return state;
		} else {
			const updatedEvent: EventCalendar = {
				year: foundEvent.year,
				month: foundEvent.month,
				event_id: foundEvent.event_id,
				event_calendar_id: foundEvent.event_calendar_id,
				dates: [...foundEvent.dates, ...action.event.dates],
			};

			return {
				...state,
				events: [updatedEvent],
			};
		}
	} else {
		return {
			...state,
			events: [...state.events, action.event],
		};
	}
});

const onClearEventDates = on(clearEventDates, (state: BookingsState, action) => {
	return {
		...state,
		events: [],
	};
});

const onSetNewBookingDateTime = on(setNewBookingDateTime, (state: BookingsState, action) => {
	return {
		...state,
		openEntitlement: {
			...state.openEntitlement,
			newBookingDate: action.newDate,
			newBookingTime: action.newTime,
		},
		events: [],
	};
});

const onClearOpenEntitlement = on(clearOpenEntitlement, (state: BookingsState, action) => {
	return {
		...state,
		openEntitlement: null,
		events: [],
	};
});

const onResetSwFailedCount = on(resetSwFailedCount, (state: BookingsState, action) => {
	return {
		...state,
		swFailedCount: null,
	};
});

const onLogout = on(logout, clearUserAction, (state: BookingsState, action) => ({
	bookings: [],
	events: [],
	openEntitlement: null,
}));

export const bookingsReducer = createReducer(
	initialState,
	onBookingReceived,
	onSetOpenEntitlement,
	onSetNewBookingDateTime,
	onClearOpenEntitlement,
	onEventDataReceived,
	onClearEventDates,
	onResetSwFailedCount,
	onLogout
);
